import { useId, useMemo } from 'react';
import { useForm, type UseFormRegister } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, FormField, Input } from '@/ui';
import { formatIban, isValidTrIban, normalizeIban } from '@/lib/iban-format';
import { ACCOUNT_CURRENCIES, BANK_LIST, bankDisplayName, TRY_CURRENCY } from './banks';
import type { SaveAccountInput } from './api';
import styles from './Accounts.module.css';

/**
 * Hesap ekleme / duzenleme cekmecesi.
 *
 * Cekmece (mobildeki Sheet'in web karsiligi) secildi, ayri bir sayfa DEGIL:
 * kullanici hesap eklerken mevcut listesine bakmaya devam edebilmeli — "bu
 * bankada zaten bir hesabim var miydi?" sorusunun cevabi arkada duruyor.
 *
 * Banka ve para birimi RADYO DUGMESI olarak isaretlenmis cip satirlari:
 * gorunusu cip, semantigi radyo. Acilir liste (select) 17 bankada secili degeri
 * gizliyor ve klavye kullanicisina fazladan bir acilma adimi ekliyor; radyo
 * grubu ok tuslariyla dolasilir ve secili olan HER ZAMAN gorunur. (Mobilde de
 * ayni gerekceyle Picker yerine cip satiri kullaniliyor.)
 *
 * FORM DURUMU ACILISTA SIFIRLANIR — ama bir efektle degil: ust bilesen bu
 * bileseni her acilista YENI BIR `key` ile cizer, React de agaci basdan kurar.
 * `reset()` cagiran bir useEffect ayni isi bir render turu gecikmeyle ve
 * "bagimlilik listesine initialValues'i koyarsam form yazilirken siniyor"
 * tuzagiyla yapardi.
 */

export type AccountFormValues = {
  bankCode: string;
  currency: string;
  accountName: string;
  iban: string;
};

/**
 * Sema bir FABRIKA (bkz. LoginScreen): mesajlar t()'den geliyor ve modul
 * seviyesinde kurulsalardi import aninda donar, dil degisiminde bayat kalirdi.
 */
const makeAccountSchema = (t: (key: string) => string) =>
  z.object({
    bankCode: z.string().min(1, { message: t('accounts.bankRequired') }),
    currency: z.string().min(1, { message: t('accounts.currencyRequired') }),
    accountName: z
      .string()
      .trim()
      .min(1, { message: t('accounts.accountNameRequired') }),
    iban: z
      .string()
      .min(1, { message: t('accounts.ibanRequired') })
      // Sekil VE mod-97 saglamasi birlikte. Yalniz uzunluga bakan bir kontrol
      // tek hanesi yanlis yazilmis bir IBAN'i kabul eder; para yanlis gider.
      .refine(isValidTrIban, { message: t('accounts.ibanInvalid') }),
  });

export type AccountFormDrawerProps = {
  open: boolean;
  onClose: () => void;
  /** Duzenlenen kaydin kimligi; yoksa yeni kayit. Yalniz baslik icin. */
  editingId?: string | undefined;
  /** Form acilis degerleri. */
  initialValues: AccountFormValues;
  /**
   * "TRY hesabi ekle" kisayolu: para birimi TRY'ye sabitlenir ve digerleri hic
   * cizilmez. Kisayola basan kullanici zaten TRY istiyor.
   */
  lockCurrencyToTry?: boolean;
  busy: boolean;
  onSubmit: (input: Omit<SaveAccountInput, 'id'>) => void;
};

export function AccountFormDrawer({
  open,
  onClose,
  editingId,
  initialValues,
  lockCurrencyToTry = false,
  busy,
  onSubmit,
}: AccountFormDrawerProps) {
  const { t } = useTranslation();
  const formId = useId();
  const schema = useMemo(() => makeAccountSchema(t), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AccountFormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues,
  });

  const currencies = lockCurrencyToTry ? [TRY_CURRENCY] : ACCOUNT_CURRENCIES;

  const submit = handleSubmit((values) => {
    onSubmit({
      bankCode: values.bankCode,
      // Sunucudaki `accountLabel`: mobil de bankanin adini boyle yaziyor.
      // Katalogtan turetiliyor, kullanicidan alinmiyor.
      bankLabel: bankDisplayName(values.bankCode),
      accountName: values.accountName.trim(),
      currency: values.currency,
      // Sunucuya HER ZAMAN normallestirilmis hali gider: ekranda bosluklu
      // gorunen deger veritabaninda bosluklu saklanirsa ayni IBAN iki farkli
      // dize olur ve karsilastirmalar sessizce basarisiz olur.
      iban: normalizeIban(values.iban),
    });
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editingId ? t('accounts.editAccount') : t('accounts.addAccount')}
      footer={
        <div className={styles.formActions}>
          <Button tone="ghost" onClick={onClose} disabled={busy}>
            {t('accounts.give')}
          </Button>
          {/* Buton cekmecenin ALT SERIDINDE, form ise govdede — `form`
              ozniteligi ikisini baglar; gizli bir submit butonu kopyalamak
              gerekmez. */}
          <Button type="submit" form={formId} loading={busy}>
            {t('accounts.save')}
          </Button>
        </div>
      }
    >
      <form id={formId} className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>{t('accounts.selectBank')}</legend>
          <div className={styles.chipRow}>
            {BANK_LIST.map((bank) => (
              <label key={bank.code} className={styles.chipOption}>
                <input
                  type="radio"
                  className={styles.chipInput}
                  value={bank.code}
                  {...register('bankCode')}
                />
                <span className={styles.chipFace}>{bank.brandName}</span>
              </label>
            ))}
          </div>
          {errors.bankCode?.message ? (
            <p className={styles.fieldError} role="alert">
              {errors.bankCode.message}
            </p>
          ) : null}
        </fieldset>

        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>{t('accounts.selectCurrency')}</legend>
          <div className={styles.chipRow}>
            {currencies.map((currency) => (
              <label key={currency} className={styles.chipOption}>
                <input
                  type="radio"
                  className={styles.chipInput}
                  value={currency}
                  {...register('currency')}
                />
                <span className={styles.chipFace}>{currency}</span>
              </label>
            ))}
          </div>
          {errors.currency?.message ? (
            <p className={styles.fieldError} role="alert">
              {errors.currency.message}
            </p>
          ) : null}
        </fieldset>

        <FormField
          label={t('accounts.accountName')}
          error={errors.accountName}
          hint={t('accounts.accountNameHint')}
          required
        >
          <Input autoComplete="off" spellCheck={false} {...register('accountName')} />
        </FormField>

        <IbanField
          label={t('accounts.accountNumber')}
          hint={t('accounts.ibanHint')}
          error={errors.iban?.message}
          register={register}
        />
      </form>
    </Drawer>
  );
}

/**
 * IBAN alani ayri bir bilesen: yazilirken DORTLU GRUPLARA bolunuyor.
 *
 * Bicimlendirme dogrudan DOM degerinin uzerine yaziliyor; ayri bir "gorunen
 * deger" durumu TUTULMUYOR. Iki durum olsaydi biri digerinden ayrisabilir ve
 * kullanicinin gordugu ile gonderilen deger farklilasabilirdi — IBAN'da bu
 * fark parayi baska hesaba gonderir.
 */
function IbanField({
  label,
  hint,
  error,
  register,
}: {
  label: string;
  hint: string;
  error: string | undefined;
  register: UseFormRegister<AccountFormValues>;
}) {
  const field = register('iban');

  return (
    <FormField label={label} error={error} hint={hint} required>
      <Input
        autoComplete="off"
        spellCheck={false}
        inputMode="text"
        placeholder="TR00 0000 0000 0000 0000 0000 00"
        {...field}
        onChange={(event) => {
          event.target.value = formatIban(event.target.value);
          void field.onChange(event);
        }}
      />
    </FormField>
  );
}
